import { officialCategoryName } from "@intelligems/ig-utils";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { constructBasePropertiesPayload } from "@src/utils/mpUtils";
import { getAiModStats } from "@src/store/slices/experienceBuilder/common/selectors/getAiModStats";
import { getAiChatSessionId } from "@src/store/slices/aiChat";

/**
 * Get the base payload for visual builder tracking events.
 * Returns null if experience is not available.
 */
export const getVisualBuilderBasePayload = createSelector(
  selectedExperience,
  getAiChatSessionId,
  getAiModStats,
  (experience, sessionId, aiModStats) => {
    if (!experience) return null;

    return {
      ...constructBasePropertiesPayload(experience),
      chat_id: sessionId,
      ai_modification_count: aiModStats.count,
      ai_modification_types: aiModStats.types,
    };
  }
);

/**
 * Get basic experience info for tracking events.
 * Returns null if experience is not available.
 * Returns camelCase keys - snake_case conversion happens in mixpanel functions.
 */
export const getExperienceBasicInfo = createSelector(
  selectedExperience,
  (experience) => {
    if (!experience) return null;
    return {
      experienceId: experience.id,
      experienceName: experience.name,
      experienceType: officialCategoryName(
        experience.category ?? "experiment",
        false
      ),
    };
  }
);

/**
 * Get a function to lookup variation name by ID.
 * Note: This is a factory function pattern - createSelector is not used
 * because memoization doesn't help the inner lookup function.
 */
export const getVariationNameById = (state: RootState) => {
  const variations = selectedVariations(state);
  return (variationId: string) => {
    return variations.find((v) => v.id === variationId)?.name ?? "Unknown";
  };
};

/**
 * Get variation by ID.
 * Note: This is a factory function pattern - createSelector is not used
 * because memoization doesn't help the inner lookup function.
 */
export const getVariationById = (state: RootState) => {
  const variations = selectedVariations(state);
  return (variationId: string) => {
    return variations.find((v) => v.id === variationId);
  };
};
